import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Eye, Network, Building2, ArrowUpRight, ArrowRight } from "lucide-react";
import { VENTURES, PROOF_INTRO } from "@/content";

// Understated monospace stat row — think Stripe metrics
function StatRow({ stats }) {
  if (!stats?.length) return null;
  return (
    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
      {stats.map((s) => (
        <div key={s.label} className="flex flex-col gap-0.5">
          <span className="font-mono text-[18px] md:text-[22px] font-semibold tracking-tight text-ink leading-none">
            {s.value}
          </span>
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-mute">
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function IconFor({ name, color = "#1A1A1A" }) {
  if (name === "eye") return <Eye size={22} color={color} strokeWidth={1.6} />;
  if (name === "network") return <Network size={22} color={color} strokeWidth={1.6} />;
  return <Building2 size={22} color={color} strokeWidth={1.6} />;
}

function badgeClasses(c) {
  switch (c) {
    case "emerald":
      return "bg-emerald-500/12 text-emerald-700 border-emerald-500/30";
    case "blue":
      return "bg-blue-500/12 text-blue-700 border-blue-500/30";
    case "slate":
    default:
      return "bg-black/8 text-ink/80 border-black/15";
  }
}

function FeaturedCard({ v }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-[24px] overflow-hidden border border-black/8 bg-white"
      data-testid={`venture-card-${v.key}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-7 p-8 md:p-12 lg:p-14 flex flex-col justify-between">
          <div>
            <span
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-mono font-semibold tracking-[0.18em] uppercase border ${badgeClasses(v.badgeColor)}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full bg-current${v.badgeColor === "slate" ? "" : " animate-pulse-soft"}`} />
              {v.badge}
            </span>
            <h3 className="mt-6 font-serif text-[42px] md:text-[60px] leading-[1.0] tracking-tight text-ink">
              {v.name}
            </h3>
            <p className="mt-5 text-ink/85 leading-[1.65] text-[16px] md:text-[17px] max-w-[56ch]">
              {v.description}
            </p>

            <StatRow stats={v.stats} />

            <div className="mt-6 flex flex-wrap gap-2">
              {v.tags?.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full font-mono text-[10px] tracking-[0.18em] uppercase border border-black/12 text-ink/75 bg-paper"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 pt-6 border-t border-black/10">
            <div>
              <div className="label-mono mb-1.5">My Role</div>
              <p className="text-ink/85 text-[14px] leading-relaxed">{v.role}</p>
            </div>
            <div>
              <div className="label-mono mb-1.5">Proof</div>
              <ul className="text-ink/85 text-[14px] leading-relaxed space-y-1">
                {v.proof?.map((pp) => (
                  <li key={pp}>· {pp}</li>
                ))}
              </ul>
            </div>
          </div>

          {v.link && (
            <div className="mt-8">
              <Link
                to={v.link}
                data-cursor="magnetic"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ink text-paper font-mono text-[11px] tracking-[0.18em] uppercase hover:bg-ink/80 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                Read the case study
                <ArrowRight size={14} strokeWidth={2.2} />
              </Link>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 min-h-[260px] lg:min-h-[460px] relative overflow-hidden">
          {v.image ? (
            <>
              <img
                src={v.image}
                alt={v.name}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            </>
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                background:
                  "radial-gradient(circle at 60% 40%, rgba(0,212,170,0.18), rgba(255,255,255,0) 60%), linear-gradient(135deg, #0F1115 0%, #1B2433 100%)",
              }}
            >
              <svg viewBox="0 0 360 360" className="w-[80%] max-w-[360px]" aria-hidden>
                <defs>
                  <radialGradient id="iris" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#00D4AA" stopOpacity="0.9" />
                    <stop offset="55%" stopColor="#0066FF" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#0E1630" stopOpacity="0.95" />
                  </radialGradient>
                </defs>
                <ellipse cx="180" cy="180" rx="170" ry="100" fill="none" stroke="#ffffff22" strokeWidth="1" />
                <ellipse cx="180" cy="180" rx="100" ry="100" fill="url(#iris)" />
                <circle cx="180" cy="180" r="32" fill="#0E1630" />
                <circle cx="170" cy="170" r="6" fill="#ffffff" opacity="0.85" />
                {[...Array(12)].map((_, i) => {
                  const a = (i / 12) * Math.PI * 2;
                  const r = 138;
                  const cx = 180 + Math.cos(a) * r;
                  const cy = 180 + Math.sin(a) * r;
                  return (
                    <g key={i}>
                      <line x1="180" y1="180" x2={cx} y2={cy} stroke="#00D4AA" strokeOpacity="0.25" strokeWidth="1" />
                      <circle cx={cx} cy={cy} r="3" fill="#00D4AA" />
                    </g>
                  );
                })}
              </svg>
              <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.24em] uppercase text-white/60 flex items-center gap-2">
                <IconFor name={v.icon} color="#00D4AA" />
                <span>{v.name} · Edge AI</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function MiniCard({ v, idx }) {
  // distinct simple SVG visuals per venture (no garish chrome golden 3D)
  const visual =
    v.key === "purpleluna" ? (
      <svg viewBox="0 0 360 220" className="w-full h-full" aria-hidden>
        <rect width="360" height="220" fill="#0F1115" />
        {[...Array(10)].map((_, i) => (
          <g key={i}>
            <circle cx={40 + i * 32} cy={110 + Math.sin(i) * 30} r="6" fill="#8B5CF6" opacity="0.85" />
            {i < 9 && (
              <line
                x1={40 + i * 32}
                y1={110 + Math.sin(i) * 30}
                x2={40 + (i + 1) * 32}
                y2={110 + Math.sin(i + 1) * 30}
                stroke="#8B5CF6"
                strokeOpacity="0.45"
                strokeWidth="1.5"
              />
            )}
          </g>
        ))}
        <text x="20" y="200" fill="#ffffff77" fontFamily="monospace" fontSize="10" letterSpacing="2">
          PURPLE · LUNA · AI
        </text>
      </svg>
    ) : (
      <svg viewBox="0 0 360 220" className="w-full h-full" aria-hidden>
        <rect width="360" height="220" fill="#0F1115" />
        {/* skyline */}
        {[...Array(18)].map((_, i) => {
          const h = 40 + ((i * 37) % 110);
          return (
            <rect
              key={i}
              x={10 + i * 20}
              y={220 - h - 20}
              width={14}
              height={h}
              fill="#FF6B35"
              opacity={0.75 - (i % 4) * 0.12}
            />
          );
        })}
        <circle cx="320" cy="50" r="22" fill="#FF8E66" />
        <text x="20" y="200" fill="#ffffff77" fontFamily="monospace" fontSize="10" letterSpacing="2">
          WHOTNOW · NIGHTLIFE
        </text>
      </svg>
    );

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.85, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-[22px] overflow-hidden border border-black/8 bg-white flex flex-col"
      data-testid={`venture-card-${v.key}`}
    >
      <div className="aspect-[16/10] w-full overflow-hidden relative">
        {v.image ? (
          <img
            src={v.image}
            alt={v.name}
            className="w-full h-full object-cover object-center"
          />
        ) : (
          visual
        )}
      </div>
      <div className="p-7 md:p-9 flex flex-col flex-1">
        <span
          className={`self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-mono font-semibold tracking-[0.18em] uppercase border ${badgeClasses(v.badgeColor)}`}
        >
          {v.badge}
        </span>
        <h3 className="mt-5 font-serif text-[28px] md:text-[34px] leading-[1.05] tracking-tight text-ink">
          {v.name}
        </h3>
        <p className="mt-3 text-ink/80 leading-[1.6] text-[14.5px] md:text-[15.5px]">
          {v.description}
        </p>
        <StatRow stats={v.stats} />
        <div className="mt-auto pt-5 border-t border-black/10 flex flex-wrap items-center justify-between gap-4">
          <span className="label-mono">{v.role}</span>
          {v.links && (
            <div className="flex flex-wrap gap-2">
              {v.links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  data-cursor="magnetic"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-paper font-mono text-[10px] tracking-[0.18em] uppercase hover:bg-ink/80 transition-colors"
                >
                  {l.label}
                  <ArrowUpRight size={11} strokeWidth={2.3} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}

export default function Proof() {
  const featured = VENTURES.find((v) => v.featured);
  const others = VENTURES.filter((v) => !v.featured);

  return (
    <section id="proof" className="relative py-24 md:py-36" data-testid="chapter-proof">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 md:mb-16 max-w-[60ch]"
        >
          <div className="label-mono mb-5">03 — {PROOF_INTRO.kicker}</div>
          <h2 className="font-serif text-[40px] md:text-[64px] leading-[1.0] tracking-tight text-ink">
            {PROOF_INTRO.title}
          </h2>
          <p className="mt-5 text-[16px] md:text-[18px] text-mute leading-[1.6] max-w-[56ch]">
            {PROOF_INTRO.sub}
          </p>
        </motion.div>

        {featured && <FeaturedCard v={featured} />}

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {others.map((v, i) => (
            <MiniCard v={v} key={v.key} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
